import { Component } from '@theme/component';
import { Wishlist } from '@theme/wishlist';
import { ThemeEvents, WishlistUpdateEvent } from '@theme/events';

/**
 * A heart-icon toggle that adds/removes the current product from the
 * client-side (localStorage) wishlist. Multiple instances for the same
 * product (a card's icon, the product page's link) stay in sync via
 * `ThemeEvents.wishlistUpdate`.
 *
 * @extends Component
 */
export class WishlistButtonComponent extends Component {
  get #productId() {
    return this.dataset.productId || '';
  }

  connectedCallback() {
    super.connectedCallback();
    this.#render(Wishlist.hasProduct(this.#productId));
    document.addEventListener(ThemeEvents.wishlistUpdate, this.#handleWishlistUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.#handleWishlistUpdate);
  }

  /**
   * @param {Event} event
   */
  handleClick = (event) => {
    event.preventDefault();

    const productId = this.#productId;
    if (!productId) return;

    const active = Wishlist.toggleProduct(productId);
    this.#render(active);

    document.dispatchEvent(new WishlistUpdateEvent({ productId, active }));
  };

  /**
   * @param {Event} event
   */
  #handleWishlistUpdate = (event) => {
    if (!(event instanceof WishlistUpdateEvent)) return;
    if (event.detail.productId !== this.#productId) return;

    this.#render(event.detail.active);
  };

  /**
   * @param {boolean} active
   */
  #render(active) {
    this.toggleAttribute('data-active', active);

    const trigger = this.querySelector('.wishlist-button__trigger');
    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-pressed', active ? 'true' : 'false');

      const label = active ? trigger.dataset.labelRemove : trigger.dataset.labelAdd;
      if (label) trigger.setAttribute('aria-label', label);
    }

    const label = this.querySelector('.wishlist-button__label');
    if (label instanceof HTMLElement) {
      label.textContent = active
        ? label.dataset.textActive || label.textContent || ''
        : label.dataset.textInactive || label.textContent || '';
    }
  }
}

if (!customElements.get('wishlist-button-component')) {
  customElements.define('wishlist-button-component', WishlistButtonComponent);
}
